import { useCallback, useMemo, useRef, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { ColDef, SelectionChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';

import { ChevronDown, Search } from 'assets/assetsV1/iconComponents';

import { Button } from 'uiToolkitV1/Button';
import { ChakraStyleInput } from 'uiToolkitV1/InputV2/ChakraStyleInput';
import { ListItemComp } from 'uiToolkitV1/ListItemComp';
import { Menu, MenuButton, MenuListNoPadding } from 'uiToolkitV1/MenuV2';
import { GenericControlAsButton } from 'uiToolkitV1/SelectV2/GenericControl';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import '../../styles/agGrid.css';
import './agGrid.css';

export interface Row {
  id: string;
  title: string;
  subtitle?: string;
}

export interface SearchableAgGridMenuProps {
  rowData: Row[];
  defaultCheckedRows?: Row[];
  onCancel?: () => void;
  onApply?: (checkedRows: Row[]) => void;
  searchRightComp: React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gridOptions?: any;
  showSearchInput?: boolean;
  optionsHeight?: string;
  placeholder?: string;
}

const CellRenderer = ({ data }: { data: Row }) => (
  <ListItemComp
    // padding="24px 0px"
    withoutBackground
    title={data.title}
    subTitle={data.subtitle}
    RightComponent={null}
    LeftComponent={null}
    subSubtitle={null}
    onClick={() => {}}
    isDisabled={false}
    active={false}
    isBoldText={false}
  />
);

export const SearchableAgGridMenu = ({
  rowData,
  onCancel,
  onApply,
  defaultCheckedRows = [],
  searchRightComp = null,
  gridOptions,
  showSearchInput = true,
  optionsHeight,
  placeholder = 'Select',
}: SearchableAgGridMenuProps) => {
  const [searchText, setSearchText] = useState('');
  const [checkedRows, setCheckedRows] = useState<Row[]>(defaultCheckedRows);
  const [temporaryCheckedRows, setTemporaryCheckedRows] = useState<Row[]>(defaultCheckedRows);
  const gridRef = useRef<AgGridReact<Row>>();

  const selectedValueString = checkedRows.map((row) => row.title).join(', ');

  const defaultColDef = useMemo<ColDef<Row>>(
    () => ({
      autoHeight: true,
      flex: 1,
    }),
    []
  );

  const columnDefs = useMemo<ColDef<Row>[]>(
    () => [
      {
        colId: 'colId',
        flex: 1,
        getQuickFilterText: (params) => params.data.title as string,
        sortable: true,
        cellRenderer: CellRenderer,
        comparator: (_valueA, _valueB, nodeA, nodeB) => {
          const checkedA = temporaryCheckedRows.find((c) => c.id === nodeA.data.id);
          const checkedB = temporaryCheckedRows.find((c) => c.id === nodeB.data.id);
          if (checkedA && checkedB) return 0;
          if (checkedA) return -1;
          if (checkedB) return 1;
          return 0;
        },
      },
    ],
    [temporaryCheckedRows]
  );

  const gridStyle = useMemo(
    () => ({
      height: showSearchInput ? 'calc(100% - 65px)' : '100%',
      width: '100%',
    }),
    [showSearchInput]
  );

  const markDefaultCheck = useCallback(() => {
    if (gridRef.current) {
      gridRef.current.api?.forEachNode((node) => {
        const checked = checkedRows.find((c) => c.id === node.data.id);
        node.setSelected(!!checked);
      });
    }
  }, [checkedRows]);

  const sort = useCallback(() => {
    gridRef.current?.api?.applyColumnState({
      state: [{ colId: 'colId', sort: 'asc' }],
      defaultState: { sort: null },
    });
  }, []);

  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent<Row>) => {
      const x = event.api.getSelectedRows();
      setTemporaryCheckedRows(x);
      sort();
    },
    [sort]
  );

  const handleCancel = (onClose) => {
    onCancel?.();
    onClose();
  };

  const handleApply = (onClose) => {
    const newCheckedRows = gridRef.current.api.getSelectedRows();
    setCheckedRows(newCheckedRows);
    onApply?.(newCheckedRows);
    onClose();
  };

  return (
    <Menu autoSelect={false} matchWidth isLazy>
      {({ onClose }) => (
        <>
          <MenuButton as={GenericControlAsButton} EndIcon={() => <ChevronDown boxSize="24px" />}>
            <TextBodyMedium
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                // maxWidth: '100%'
              }}
              color={selectedValueString ? 'text.g1' : 'text.g3'}
            >
              {selectedValueString || placeholder}
            </TextBodyMedium>
          </MenuButton>
          <MenuListNoPadding border="none" boxShadow="0px 2px 8px 0px rgba(0, 0, 0, 0.08)">
            <Box borderRadius="8px" overflow="auto" pt="0 !important">
              <Box height={optionsHeight ?? '260px'} minW="300px">
                {showSearchInput && (
                  <Flex px={2} py={3} borderBottom="1px solid" borderColor="border.CFD7DF">
                    <ChakraStyleInput
                      leftComponent={<Search />}
                      placeholder="Search Views"
                      value={searchText}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
                    />
                    {searchRightComp}
                  </Flex>
                )}
                <div style={gridStyle} className="ag-theme-alpine ag-theme-searchable-grid-style">
                  <AgGridReact
                    ref={gridRef}
                    columnDefs={columnDefs}
                    rowData={rowData}
                    animateRows
                    rowSelection={{
                      enableSelectionWithoutKeys: true,
                      mode: 'multiRow',
                      enableClickSelection: true,
                    }}
                    onSelectionChanged={onSelectionChanged}
                    headerHeight={0}
                    cacheQuickFilter
                    quickFilterText={searchText}
                    defaultColDef={defaultColDef}
                    onFirstDataRendered={markDefaultCheck}
                    gridOptions={gridOptions}
                  />
                </div>
              </Box>

              <Flex py={3} px={4} bg="surface.F5F7F9" borderTop="1px solid" borderColor="border.CFD7DF">
                <Button variant="tertiary" onClick={() => handleCancel(onClose)}>
                  Cancel
                </Button>
                <Button ml="auto" onClick={() => handleApply(onClose)}>
                  Apply
                </Button>
              </Flex>
            </Box>
          </MenuListNoPadding>
        </>
      )}
    </Menu>
  );
};
